import { lazy, useContext, useState } from "react";
import { Button, Fade, Grid, Tooltip, Typography } from "@mui/material";

import { useUserData } from "src/context/userContext";
import { KeikkaDataContext } from "src/context/KeikkaDataContext";
import { useStepLogData } from "src/context/StepLogContext";
import { formattedPhoneString } from "src/components/js/statics1";
import {
  showBetoniToimittaja,
  showPumppuToimittaja,
  showTilausErrors,
  showVehicle,
} from "src/data/ifs/showIfs";

import IconsContainer from "src/components/icons/others/IconsContainer";
import MySkeleton from "src/components/layout/MySkeleton";
import Yhteystiedot from "./Yhteystiedot";
import ShowChildren from "src/components/system/ShowChildren";
import LazyLoader from "src/components/layout/Progress/LazyLoader";

const EditPersonModal = lazy(() => import("src/components/person/editPerson/EditPersonModal"));

const styles = {
  nimi: {
    display: "block",
    transition: "all 1.4s ease-in-out",
    height: "20px",
    overflow: "hidden",
  },
};

export default function HenkiloRow() {
  const { keikkaData, reloadKeikkaData } = useContext(KeikkaDataContext) || {};

  const { stepLog } = useStepLogData();
  const _keikkaContactPerson = keikkaData.keikkaContactPerson;
  const [editIsOpen, setEditIsOpen] = useState(false);
  const [editPersonIsOpen, setEditPersonIsOpen] = useState(false);
  const { user } = useUserData();

  const onEditClick = () => {
    if (keikkaData.keikkaId > 0) {
      if (
        _keikkaContactPerson.personId == user.personId &&
        _keikkaContactPerson.personError().errorTxt
      ) {
        // jos personError ja personId on sama kuin keikkaContactPersonId niin avaa editPerson
        // tämä pakottaa käyttäjän korjaamaan virheen omissa tiedoissa ennen kuin voi nähdä tilauksen muita yhteystietoja
        setEditPersonIsOpen(true);
        //setEditIsOpen(true);
      } else {
        setEditIsOpen(true);
      }
    }
  };
  const handleClose = () => setEditIsOpen(false);

  const handleEditPersonClose = () => setEditPersonIsOpen(false);

  const handleEditPersonChange = async () => {
    setEditPersonIsOpen(false);
    await reloadKeikkaData();
  };

  function getErrorTxt(keikkaData) {
    return _keikkaContactPerson.personError(keikkaData.auth.edit).txt;
  }
  function getErrorObj(keikkaData) {
    const b = showTilausErrors(keikkaData);
    if (!b) return null;
    const c = _keikkaContactPerson.personError(keikkaData.auth.edit);
    return c;
  }

  const hasError = getErrorTxt ? true : false;
  return (
    <>
      <LazyLoader>
        {editPersonIsOpen && (
          <EditPersonModal
            personId={user ? Number(user.personId) : 0}
            isOpen={editPersonIsOpen}
            onClose={handleEditPersonClose}
            onChange={handleEditPersonChange}
          />
        )}
      </LazyLoader>
      <Yhteystiedot isOpen={editIsOpen} onClose={handleClose} />
      <div
        style={{
          padding: getErrorTxt(keikkaData) ? "10px" : "0px",
          transition: "all 0.6s ease-in-out",
        }}>
        <Tooltip
          disableInteractive
          title={getErrorObj(keikkaData)?.errorTxt || ""}
          placement="top-end"
          arrow>
          <Button
            variant={keikkaData.auth.edit && getErrorTxt(keikkaData) ? "outlined" : "text"}
            fullWidth
            onClick={onEditClick}
            sx={{ minHeight: "50px", margin: 0, padding: 0, transition: "all 0.9s ease-in-out" }}
            aria-label="Muokkaa henkilöä">
            <Grid container className="stepRow" sx={{ width: "100%" }}>
              <Grid size={{ xs: 3 }} sx={{ textAlign: "right" }}>
                <Typography
                  variant="h6"
                  sx={{ paddingRight: "5px" }}
                  noWrap
                  color="primary"
                  className="fixPrint transitionElement">
                  Yhteys:
                </Typography>
              </Grid>
              {keikkaData.isLoading ? (
                <MySkeleton width="70%" height="35px" />
              ) : (
                <>
                  <Fade in={true} timeout={500}>
                    <Grid size={{ xs: 7 }} sx={{ textAlign: "left" }}>
                      {keikkaData.auth.read && (
                        <div style={{ paddingTop: "6px" }}>
                          <div className="divTable">
                            <div className="divTableBody">
                              <div className="divTableRow">
                                {hasError && showTilausErrors(keikkaData) ? (
                                  <>
                                    <div style={{ position: "absolute" }}>
                                      <Typography
                                        color="warning.main"
                                        className="fixPrint divTableCell transitionElement">
                                        {getErrorTxt(keikkaData)}
                                      </Typography>
                                    </div>
                                    <div style={{ height: "25px" }}></div>
                                  </>
                                ) : (
                                  <>
                                    <Typography
                                      color="primary"
                                      className="fixPrint divTableCell transitionElement">
                                      Tilaus:
                                    </Typography>
                                    <Typography
                                      color="primary"
                                      className="fixPrint"
                                      sx={styles.nimi}>
                                      {_keikkaContactPerson.personFirstName}{" "}
                                      {_keikkaContactPerson.personLastName}
                                    </Typography>
                                    <Typography
                                      color="primary"
                                      className="fixPrint divTableCell transitionElement"
                                      noWrap>
                                      {formattedPhoneString(_keikkaContactPerson.personPhone)}
                                    </Typography>
                                  </>
                                )}
                              </div>

                              {showPumppuToimittaja(keikkaData) && (
                                <>
                                  <div className="divTableRow">
                                    <Typography
                                      color="primary"
                                      className="fixPrint divTableCell transitionElement">
                                      Pumppu:
                                    </Typography>
                                    <Typography
                                      color="primary"
                                      className="fixPrint divTableCell"
                                      sx={styles.nimi}>
                                      {keikkaData.pumppuToimittaja.asiakasNimi}
                                    </Typography>
                                    <Typography
                                      color="primary"
                                      className="fixPrint divTableCell transitionElement"
                                      noWrap>
                                      {formattedPhoneString(
                                        keikkaData.pumppuToimittaja.asiakasContactPersonPhone
                                      )}
                                    </Typography>
                                  </div>

                                  <ShowChildren
                                    source="HenkiloRow.jsx"
                                    criteriaBool={showVehicle(keikkaData, user, stepLog)}
                                    criteriaTxt="showVehicle(keikkaData, user, stepLog)">
                                    <div className="divTableRow">
                                      <div className="divTableCell transitionElement" />
                                      <Typography
                                        color="primary"
                                        className="fixPrint divTableCell"
                                        sx={styles.nimi}
                                        variant="body2">
                                        {keikkaData.pumppu.vehicle.vehicleRegNo}
                                      </Typography>
                                      <Typography
                                        color="primary"
                                        className="fixPrint divTableCell transitionElement"
                                        variant="body2"
                                        noWrap>
                                        {keikkaData.pumppu.pumppariList &&
                                          keikkaData.pumppu.pumppariList.map((pumppari, index) => {
                                            return (
                                              <span key={pumppari.personId}>
                                                {formattedPhoneString(pumppari.personPhone)}
                                                {index < keikkaData.pumppu.pumppariList.length - 1
                                                  ? ", "
                                                  : ""}
                                              </span>
                                            );
                                          })}
                                      </Typography>
                                    </div>
                                  </ShowChildren>
                                </>
                              )}
                              <ShowChildren
                                source="HenkiloRow.jsx"
                                criteriaBool={showBetoniToimittaja(keikkaData)}
                                criteriaTxt="showBetoniToimittaja(keikkaData)">
                                <div className="divTableRow">
                                  <Typography
                                    color="primary"
                                    className="fixPrint divTableCell transitionElement">
                                    Betoni:
                                  </Typography>
                                  <Typography
                                    color="primary"
                                    className="fixPrint divTableCell"
                                    sx={styles.nimi}>
                                    {keikkaData.betoniToimittaja.asiakasNimi}
                                  </Typography>
                                  <Typography
                                    color="primary"
                                    className="fixPrint divTableCell transitionElement"
                                    noWrap>
                                    {formattedPhoneString(
                                      keikkaData.betoniToimittaja.asiakasContactPersonPhone
                                    )}
                                  </Typography>
                                </div>
                              </ShowChildren>
                            </div>
                          </div>
                        </div>
                      )}
                    </Grid>
                  </Fade>
                  <Fade in={true} timeout={500}>
                    <Grid size={{ xs: 2 }} className="frontPageEditIcon">
                      <IconsContainer
                        showWarning={hasError && showTilausErrors(keikkaData)}
                        showEdit={keikkaData.auth.edit}
                        editOpacity={0.5}
                      />
                    </Grid>
                  </Fade>
                </>
              )}
            </Grid>
          </Button>
        </Tooltip>
      </div>
    </>
  );
}
